<template>
    <div class="leftCardContainer">
        <div class="rightSideTitleActions">
            <span class="leftTitle">{{ $t('Submitted') + ' ' + $tc('File',2) }}</span>
            <div class="actionsContainer">
                <div class="searchContainer">
                    <v-icon> search </v-icon>
                    <InputField 
                        inputType="text" 
                        :value="search" 
                        :placeholder="$t('Search')" 
                        :inputStyle="{
                            borderRadius: '0px',
                            width: '130px',
                            borderTop: 'none',
                            borderRight: 'none',
                            borderLeft: 'none',
                            borderBottom: '1px solid rgba(118, 118, 118, 0.50)',
                        }" 
                        @input="handleSearch($event)" 
                    />
                </div>
            </div>
        </div>
        <div class="tabsContainer">
            <Tabs 
                :activeTab="activeTab" 
                :tabs="tabs" 
                :centerTabs="false" 
                :tabs-styling="tabsStyling"
                :tabs-labels-styling="tabsLabelsStyling" 
                @startTabChange="setActiveTab" 
            />
        </div>
        <div class="tableDivContainer">
            <Table
                :scroll-id="tableId"
                :checkbox="false"
                :loading="loading"
                :headers="headers"
                :items="imports"
                :sortable="true"
                :emitRowClickBusEvent="true"
                :has-imports-buttons="true"
                :has-delete-permission="userHasPermissionTo('destroy')"
                @validate="$emit('validate', $event)"
                @onScrollBottom="onScrollBottom"
                @onScrollTop="onScrollTop"
                @sortClick="onSortClick"
            />
        </div>
        <LoadingSpinner :active.sync="deleteLoading" :is-full-page="true" color="var(--app-primary-base)" />
    </div>
</template>
  
<script>
    import { mapActions } from "vuex";
    import { bus } from "@/main.js";
    import Table from "@/newComponents/_Molecules/Table.vue";
    import InputField from "@/newComponents/_Atoms/InputField.vue";
    import DialogContent from "@/newComponents/_Molecules/DialogContent.vue";
    import { managementDatatableMixin } from "@/mixins/management/managementDatatableMixin";
    import CustomButton from '@/newComponents/_Atoms/CustomButton';
    import DialogChoice from '@/newComponents/_Molecules/DialogChoice.vue';
    import Tabs from "@/newComponents/_Atoms/Tabs.vue";
    import LoadingSpinner from "vue-loading-overlay";

    export default {
        name: "TableViewSubmittedFiles",

        mixins: [managementDatatableMixin],

        components: {
            Table,
            InputField,
            DialogContent,
            CustomButton,
            DialogChoice,
            Tabs,
            LoadingSpinner
        },

        watch: {
            activeTab(newValue){
                this.handleTabChange(newValue);
                this.$emit('clear-conflicts-table');
            },
            tableFilters(){
                this.getInitialImports();
            }
        },

        mounted() {
            bus.$on("editable-row-delete", this.handleDelete);
            bus.$on('on-row-click', data => {
                this.setImportObj(data);
            });
            this.getInitialData();
            this.interval = setInterval(() => {
                this.updateTable();
            }, 10000);
        },

        destroyed() {
            bus.$off('on-row-click');
            bus.$off("editable-row-delete");
            clearInterval(this.interval);
            this.interval = null;
        },

        data() {
            return {
                tabsStyling: {
                    'margin': '0px 0px 8px 0px',
                    'width': '100%',
                    'height': '32px',
                },
                tabsLabelsStyling: {
                    'text-transform': 'none',
                    'font-size': '10px',
                    'letter-spacing': '0',
                },
                interval: null,
                tableId: '1',
                search: null,
                loading: false,
                scrollTop: 0,
                sortOrder: null,
                scrollHeight: 0,
                pageNumber: 1,
                pagination: 15,
                currentPageTop: 1,
                currentPageBot: 1,
                lastPage: 2,
                imports: [],
                activeTab: 0,
                tableFilters: {
                    state: 'active'
                },
                templateOptions: [],
                deleteLoading: false,
                timestamp: this.getFormattedTimestamp()
            };
        },

        computed: {
            headers() {
                return [
                    { text: this.$i18n.tc("Layer", 1), varName: "type" },
                    { text: this.$i18n.tc("Projection", 1), varName: "projection" },
                    { text: this.$i18n.tc("Template", 1) , varName: "template_id" },
                    { text: `${this.$i18n.tc("File", 1)} ${this.$i18n.t("Name")}`, varName: "original_file_name" },
                    { text: this.$i18n.t("Updated") , varName: "updated_at" },
                    { text: this.$i18n.tc("User", 1) , varName: "user_name" },
                    { text: this.$i18n.tc("Step", 1) , varName: "step" },
                    { text: this.$i18n.t("Status") , varName: "status" }
                ]
            },
            tabs() {
                const tabs = [
                    { label: this.$i18n.t("Active") },
                    { label: this.$i18n.t("History") },
                ];

                return tabs;
            },

        },

        methods: {
            ...mapActions(["GET_ALL_IMPORTS", "deleteImport", "fetchAllImportTemplates"]),

            async updateTable(){
                if(this.activeTab){
                    return;
                }
                try {
                    const res = await this.GET_ALL_IMPORTS({timestamp: this.timestamp, queryParams: null, filters: {state: null }});
                    this.timestamp = res.headers['timestamp_server'];
                    res.data.data.forEach((resItem) => {
                        // Find the corresponding object in this.imports
                        const importItem = this.imports.find((importItem) => importItem.id === resItem.id);
                        if (importItem) {
                            // Update the properties of the found object
                            Object.keys(resItem).forEach((key) => {
                                if(key === 'template_id'){
                                    let template = this.templateOptions.find(template => template.id === resItem[key]);
                                    importItem[key] =  template ? template.name : resItem[key];
                                    return;
                                }
                                if(key === 'updated_by_user_details'){
                                    let userName = JSON.parse(resItem[key]);
                                    importItem.user_name = userName ? `${userName.first_name} ${userName.last_name}` : '';
                                    return;
                                }
                                if(key === 'status'){
                                    importItem[key] = this.getImportStatus(resItem);
                                    return;
                                }
                                if(key === 'type'){
                                    importItem.filteredType = resItem[key];
                                    importItem[key] = resItem[key].replace(/^fc_/, '');
                                    return;
                                }
                                importItem[key] = resItem[key];
                            });
                        }
                    });
                } catch (error) {
                    console.error(error);
                }
            },

            getFormattedTimestamp() {
                const now = new Date();

                const year = now.getFullYear();
                const month = String(now.getMonth() + 1).padStart(2, '0');  // Months are zero-based
                const day = String(now.getDate()).padStart(2, '0');
                const hours = String(now.getHours()).padStart(2, '0');
                const minutes = String(now.getMinutes()).padStart(2, '0');
                const seconds = String(now.getSeconds()).padStart(2, '0');

                return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
            },

            async handleDelete(obj){
                try{
                    this.deleteLoading = true;
                    await this.deleteImport(obj.uuid);
                    this.$toasted.global.toast_success(this.$i18n.t("DeleteImportSuccess"));
                }catch(error){
                    this.$toasted.global.toast_error(error);
                }finally{
                    this.deleteLoading = false;
                }
            },

            setImportObj(obj){
                this.$emit('set-import-obj',obj)
            },

            async getInitialData(){
                await this.getTemplates();
                await this.getInitialImports();
            },

            async getTemplates() {
                this.loading = true;
                try {
                    this.templateOptions = await this.fetchAllImportTemplates();
                } catch (error) {
                    this.$toasted.global.toast_error(error);
                } finally {
                    this.loading = false;
                }
            },

            setActiveTab(index){
                this.activeTab = index;
            },

            handleTabChange(index) {
                if (index === 0) {
                    //this is for the active tab
                    this.tableFilters = {
                        state: 'active'
                    }
                } else {
                    //this is for the history tab
                    this.tableFilters = {
                        state: 'history'
                   }
                }
            },

            getInitialImports() {
                this.currentPageBot = 1
                this.currentPageTop = 1
                let queryParams = {
                    "page[number]": 1,
                    paginate: 25
                };
        
                if (this.search !== null) {
                    queryParams["filter[search]"] = this.search;
                }
        
                if (this.sortOrder !== null) {
                    queryParams["sort"] = this.sortOrder;
                }
                this.getAllImports(queryParams);
            },

            async getAllImports(queryParams, scrollDirection = null) {
                try {
                    this.loading = true;
                    const res = await this.GET_ALL_IMPORTS({timestamp: this.timestamp, queryParams, filters: this.tableFilters});
                    this.timestamp = res.headers['timestamp_server'];
                    const imports = res.data;
                    const processedImports = imports.data.map(object => {
                        let template = this.templateOptions.find(template => template.id === object.template_id);
                        object.template_id = template ? template.name : object.template_id;
                        object.status = this.getImportStatus(object);
                        if(object.updated_by_user_details){
                            let userName = JSON.parse(object.updated_by_user_details);
                            object.user_name = `${userName.first_name} ${userName.last_name}`;   
                        }
                        object.filteredType = object.type;
                        object.type = object.type.replace(/^fc_/, '');
                        return object;
                    });
                    imports.data = processedImports;
                    this.processImportsData(imports, scrollDirection);
                } catch (error) {
                    this.$toasted.global.toast_error(error);
                }finally {
                    this.loading = false;
                    this.$nextTick(() => this.updateLocalPagination(this.paginationFromParent));
                }
            },

            getImportStatus(obj){
                let finalStatus = '';
                if(obj.step === 'save_temporary_table' && obj.total_conflicted_objects !== 0 && obj.total_objects){
                    finalStatus = this.$i18n.tc("Conflict", 2);
                }else if((obj.step === 'save_temporary_table' && obj.total_conflicted_objects === 0) || obj.step === 'import_to_database'){
                    finalStatus = this.$i18n.t("Success");
                }else{
                    finalStatus = obj.status;
                }
                return finalStatus;
            },

            processImportsData(importsData, scrollDirection = null) {
                let allImports = null;
                let currentScroll = null;
                let rowElement = null;
                switch (scrollDirection) {
                    case 'top':
                        this.lastPage = importsData.meta.last_page;
                        allImports = [...importsData.data, ...this.imports];
                        this.imports = Array.from(new Set(allImports.map(a => a.id)))
                            .map(id => { return allImports.find(a => a.id === id) })
                        if (this.imports.length > 75) {
                            this.imports.splice(-(this.imports.length - 75));
                            this.currentPageBot--;
                        }

                        currentScroll = document.getElementById(this.tableId).scrollTop;
                        rowElement = document.getElementById("rowId");
                        if (rowElement) {
                            const rowHeight = rowElement.clientHeight;
                            for (let i = 0; i < 25; i++) {
                            const offset = rowHeight * i;
                            document.getElementById(this.tableId).scrollTo({
                                top: currentScroll + offset,
                                behavior: "smooth",
                            });
                            }
                        }
                    break;
                    case 'bottom':
                        this.lastPage = importsData.meta.last_page;
                        allImports = [...this.imports, ...importsData.data];
                        this.imports = Array.from(new Set(allImports.map(a => a.id)))
                            .map(id => { return allImports.find(a => a.id === id) })
                        if (this.imports.length > 75) {
                            this.imports = this.imports.slice(this.imports.length - 75);
                        }
                        currentScroll = document.getElementById(this.tableId).scrollTop;
                        rowElement = document.getElementById("rowId");
                        if (rowElement) {
                            const rowHeight = rowElement.clientHeight;
                            for (let i = 0; i < 2; i++) {
                            const offset = rowHeight * i;
                            document.getElementById(this.tableId).scrollTo({
                                top: currentScroll - offset,
                                behavior: "smooth",
                            });
                            }
                        }
                    break;
                    default:
                        this.imports = importsData.data;
                        this.lastPage = importsData.meta.last_page;
                    break;
                }
            },

            getImportsBottom() {
                this.pagination = 25;
                if (this.imports.length >= 75) {
                    this.currentPageTop++;
                }
                this.currentPageBot++;

                let queryParams = {
                    "page[number]": this.currentPageBot,
                    paginate: this.pagination,
                };

                if (this.search !== null) {
                    queryParams["filter[search]"] = this.search;
                }

                if (this.sortOrder !== null) {
                    queryParams["sort"] = this.sortOrder;
                }
                this.getAllImports(queryParams, 'bottom');
            },

            async getImportsTop() {
                this.pagination = 25;
                this.currentPageTop--;

                let queryParams = {
                    "page[number]": this.currentPageTop,
                    paginate: this.pagination,
                };

                if (this.search !== null) {
                    queryParams["filter[search]"] = this.search;
                }

                if (this.sortOrder !== null) {
                    queryParams["sort"] = this.sortOrder;
                }
                this.getAllImports(queryParams, 'top');
            },

            handleSearch: _.debounce(function (event) {
                if (event === "") {
                    this.search = null;
                } else {
                    this.search = event;
                }
                this.imports = [];
                this.getInitialImports();

            }, 1000),
    
            onScrollBottom() {
                if (this.currentPageBot !== this.lastPage) {
                    this.pageNumber++;
                    this.getImportsBottom();
                }
            },
        
            onScrollTop() {
                if (this.currentPageTop !== 1) {
                    this.pageNumber--;
                    this.getImportsTop();
                }
            },

            onSortClick(sortOrder, index) {
                this.imports = []
                if (sortOrder === "asc") {
                    this.sortOrder = this.headers[index].varName;
                } else if (sortOrder === "desc") {
                    this.sortOrder = `-${this.headers[index].varName}`;
                } else {
                    this.sortOrder = sortOrder;
                }
                this.getInitialImports();
            },
        },
    };
</script>
  
<style scoped>
    .searchContainer {
        display: flex;
        align-items: center;
    }
    
    .tableDivContainer {
        height: calc(92% - 55px);
    }
    
    .tabsContainer {
        height: calc(8% + 19px);
        display: flex;
        margin-top: 5px;
        border-bottom: 1px solid #DBDBE0;
    }
    
    .leftCardContainer {
        width: 100%;
        height: 100%;
        border-radius: 8px;
        background: #fff;
        box-shadow: 0px 2px 2px 0px rgba(0, 0, 0, 0.25);
        padding: 20px 28px;
    }
    
    .actionsContainer {
        display: flex;
        align-items: center;
        gap: 3px;
    }
    
    .leftTitle {
        color: #767676;
        font-family: Lato;
        font-size: 10px;
        font-style: normal;
        font-weight: 800;
        line-height: 20px;
        text-transform: uppercase;
    }
    
    .buttons-container-custom {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 4px 20px;
    }
  
    .cancel-button-custom {
        background: transparent;
        border-radius: 0.25rem;
        border: 0.609px solid var(--app-primary-base);
    }
  
    .rightSideTitleActions {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
    }
</style>
  


// WEBPACK FOOTER //
// src/newComponents/Data/Imports/TableViewSubmittedFiles.vue